import { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { TournamentBracket } from "@/components/TournamentBracket";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Trash2, Trophy, CalendarDays, Loader2, Plus, Users, ExternalLink,
  RefreshCw, Edit, Undo2, Redo2, FileDown, FileText, CheckCircle, Clock, UserPlus, GripVertical
} from "lucide-react";
import { exportBracketPDF } from "@/lib/pdfExport";
import { buildPdfVenue } from "@/lib/pdfVenue";

const ROUND_LABELS = {
  R256: "Turno Prel.", R128: "128-esimi", R64: "64-esimi", R32: "32-esimi",
  R16: "16-esimi", QF: "Quarti", SF: "Semifinale", F: "Finale",
};
const DRAW_SIZES = [2, 4, 8, 16, 32, 64, 128, 256];

const BracketPanel = ({ tournament, venue }) => {
  const [selectedCat, setSelectedCat] = useState(tournament.categories[0]?.category_id || "");
  const [drawData, setDrawData] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [catPlayers, setCatPlayers] = useState([]);
  const [editingSlot, setEditingSlot] = useState(null);
  const [selectedPlayerId, setSelectedPlayerId] = useState("");
  const [showEmptyDialog, setShowEmptyDialog] = useState(false);
  const [emptySize, setEmptySize] = useState("8");
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [winnerId, setWinnerId] = useState("");
  const [matchScore, setMatchScore] = useState("");
  const [dialogMode, setDialogMode] = useState("result");
  const [schedDate, setSchedDate] = useState("");
  const [schedTime, setSchedTime] = useState("");
  const [schedCourt, setSchedCourt] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [scheduling, setScheduling] = useState(false);
  const [allMatches, setAllMatches] = useState([]);
  const [swapPosition, setSwapPosition] = useState("player1");
  const [swapPlayerId, setSwapPlayerId] = useState("");
  const [generatingAll, setGeneratingAll] = useState(false);
  const [allGenerationResult, setAllGenerationResult] = useState(null); // {generated, errors}
  const [confirmAction, setConfirmAction] = useState(null); // { type: "generate"|"clear"|"empty", label: string }
  const [historyInfo, setHistoryInfo] = useState({ can_undo: false, can_redo: false });
  const [showSeedDialog, setShowSeedDialog] = useState(false);
  const [seedMode, setSeedMode] = useState("auto"); // "auto" or "manual"
  const [manualSeeds, setManualSeeds] = useState([]); // [{player_id, name}]
  const [manualSeedsMap, setManualSeedsMap] = useState({}); // { "4.NC": [{player_id, name, ranking}], ... }
  const [showRankingDialog, setShowRankingDialog] = useState(false);
  const [qualificatiMap, setQualificatiMap] = useState({}); // { "4.NC": 2, "4.x": 1, ... }
  const [showNotesDialog, setShowNotesDialog] = useState(false);
  const [bracketNotes, setBracketNotes] = useState(tournament?.pdf_bracket_notes || "");
  const [savingNotes, setSavingNotes] = useState(false);

  useEffect(() => {
    setBracketNotes(tournament?.pdf_bracket_notes || "");
  }, [tournament?.pdf_bracket_notes]);

  const saveBracketNotes = async () => {
    setSavingNotes(true);
    try {
      await api.put(`/tournaments/${tournament.tournament_id}`, { pdf_bracket_notes: bracketNotes });
      tournament.pdf_bracket_notes = bracketNotes; // optimistic local sync
      toast.success("Note salvate");
      setShowNotesDialog(false);
    } catch (e) {
      toast.error("Errore nel salvataggio delle note");
    } finally {
      setSavingNotes(false);
    }
  };

  const fetchDraw = useCallback(async () => {
    if (!selectedCat) return;
    try {
      const [data, pl, hist] = await Promise.all([
        api.get(`/tournaments/${tournament.tournament_id}/categories/${selectedCat}/draw`),
        api.get(`/tournaments/${tournament.tournament_id}/categories/${selectedCat}/players`),
        api.get(`/tournaments/${tournament.tournament_id}/categories/${selectedCat}/draw/history`),
      ]);
      setDrawData(data);
      setCatPlayers(pl);
      setHistoryInfo(hist);
      // Load all matches for conflict detection
      try {
        const sched = await api.get(`/tournaments/${tournament.tournament_id}/schedule`);
        setAllMatches(sched.matches || []);
      } catch { setAllMatches([]); }
    } catch (e) { setDrawData(null); setCatPlayers([]); console.error("Failed to load draw:", e); }
  }, [selectedCat, tournament.tournament_id]);
  useEffect(() => { fetchDraw(); }, [fetchDraw]);

  // Build conflict map
  const conflictSet = useMemo(() => {
    const counts = {};
    for (const m of allMatches) {
      if (m.scheduled_date && m.scheduled_time && m.scheduled_court != null) {
        const key = `${m.scheduled_date}|${m.scheduled_time}|${m.scheduled_court}`;
        counts[key] = (counts[key] || 0) + 1;
      }
    }
    const conflicts = new Set();
    for (const [key, count] of Object.entries(counts)) {
      if (count > 1) conflicts.add(key);
    }
    return conflicts;
  }, [allMatches]);

  // Merge schedule data into draw matches for web bracket display
  const enrichedMatches = useMemo(() => {
    if (!drawData?.matches) return [];
    const schedMap = {};
    for (const sm of allMatches) {
      if (sm.match_id) schedMap[sm.match_id] = sm;
    }
    return drawData.matches.map(m => {
      const sched = schedMap[m.match_id];
      if (sched) {
        return {
          ...m,
          scheduled_date: sched.scheduled_date || m.scheduled_date,
          scheduled_time: sched.scheduled_time || m.scheduled_time,
          scheduled_court: sched.scheduled_court ?? m.scheduled_court,
        };
      }
      return m;
    });
  }, [drawData, allMatches]);

  const generateDraw = async () => {
    setGenerating(true);
    try { const r = await api.post(`/tournaments/${tournament.tournament_id}/categories/${selectedCat}/draw`); toast.success(`Tabellone casuale generato! ${r.total_matches} partite`); fetchDraw(); } catch (e) { toast.error("Servono almeno 2 giocatori"); } finally { setGenerating(false); }
  };

  const generateSeededDraw = async () => {
    setGenerating(true);
    try {
      const msm = {};
      for (const [sec, seeds] of Object.entries(manualSeedsMap)) {
        if (seeds && seeds.length > 0) {
          msm[sec] = seeds.map(s => s.player_id);
        }
      }
      const payload = {
        auto_seed: false,
        qualificati_per_sezione_map: qualificatiMap,
        manual_seeds_map: msm,
      };
      const r = await api.post(`/tournaments/${tournament.tournament_id}/categories/${selectedCat}/draw/seeded`, payload);
      toast.success(`Tabellone con TdS generato! ${r.total_matches} partite, ${(r.sections || []).length} sezioni`);
      setShowSeedDialog(false);
      fetchDraw();
    } catch (e) { toast.error(e.message || "Errore nella generazione"); }
    finally { setGenerating(false); }
  };

  const generateRankingDraw = async () => {
    setGenerating(true);
    try {
      const r = await api.post(`/tournaments/${tournament.tournament_id}/categories/${selectedCat}/draw/ranking`, { qualificati_per_sezione_map: qualificatiMap });
      toast.success(`Tabellone per ranking generato! ${r.total_matches} partite, ${(r.sections || []).length} sezioni`);
      setShowRankingDialog(false);
      fetchDraw();
    } catch (e) { toast.error(e.message || "Errore nella generazione"); }
    finally { setGenerating(false); }
  };

  const openSeedDialog = () => {
    setSeedMode("manual");
    const rv = { "1.1": 100, "1.2": 99, "1.3": 98, "1.4": 97, "1.5": 96, "1.6": 95, "2.1": 90, "2.2": 89, "2.3": 88, "2.4": 87, "2.5": 86, "2.6": 85, "2.7": 84, "2.8": 83, "3.1": 80, "3.2": 79, "3.3": 78, "3.4": 77, "3.5": 76, "3.6": 75, "4.1": 70, "4.2": 69, "4.3": 68, "4.4": 67, "4.5": 66, "4.6": 65, "4.NC": 50 };
    const secMap = { "4.NC": ["4.NC"], "4.x": ["4.6","4.5","4.4","4.3","4.2","4.1"], "3.x": ["3.6","3.5","3.4","3.3","3.2","3.1"], "2.x": ["2.8","2.7","2.6","2.5","2.4","2.3","2.2","2.1"], "1.x": ["1.6","1.5","1.4","1.3","1.2","1.1"] };
    // Build per-section default seeds
    const sections = {};
    for (const p of catPlayers) {
      const r = p.ranking || "4.NC";
      for (const [sec, ranks] of Object.entries(secMap)) {
        if (ranks.includes(r)) { (sections[sec] = sections[sec] || []).push(p); break; }
      }
    }
    const defaultSeedsMap = {};
    for (const [sec, players] of Object.entries(sections)) {
      const sorted = [...players].sort((a, b) => (rv[b.ranking] || 0) - (rv[a.ranking] || 0));
      const n = sorted.length;
      const nts = n < 4 ? Math.min(2, n) : n < 8 ? 2 : n < 16 ? 4 : n < 32 ? 8 : 16;
      defaultSeedsMap[sec] = sorted.slice(0, nts).map(p => ({ player_id: p.player_id, name: p.name, ranking: p.ranking }));
    }
    setManualSeedsMap(defaultSeedsMap);
    // Legacy
    const sorted = [...catPlayers].sort((a, b) => (rv[b.ranking] || 0) - (rv[a.ranking] || 0));
    const n = catPlayers.length;
    const nts = n < 4 ? 2 : n < 8 ? 2 : n < 16 ? 4 : n < 32 ? 8 : 16;
    setManualSeeds(sorted.slice(0, Math.min(nts, sorted.length)).map(p => ({ player_id: p.player_id, name: p.name, ranking: p.ranking })));
    setShowSeedDialog(true);
  };

  const addManualSeed = (pid) => {
    const p = catPlayers.find(pl => pl.player_id === pid);
    if (p && !manualSeeds.find(s => s.player_id === pid)) {
      setManualSeeds([...manualSeeds, { player_id: p.player_id, name: p.name, ranking: p.ranking }]);
    }
  };

  const removeManualSeed = (pid) => {
    setManualSeeds(manualSeeds.filter(s => s.player_id !== pid));
  };

  const moveSeed = (idx, dir) => {
    const arr = [...manualSeeds];
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= arr.length) return;
    [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
    setManualSeeds(arr);
  };

  const createEmptyDraw = async () => {
    setGenerating(true);
    try {
      const r = await api.post(`/tournaments/${tournament.tournament_id}/categories/${selectedCat}/draw/empty`, { draw_size: parseInt(emptySize) });
      toast.success(`Tabellone creato (${r.draw_size} giocatori)`);
      setShowEmptyDialog(false);
      fetchDraw();
    } catch (e) { toast.error("Errore nella creazione"); }
    finally { setGenerating(false); }
  };

  const clearDraw = async () => {
    setClearing(true);
    try {
      await api.del(`/tournaments/${tournament.tournament_id}/categories/${selectedCat}/draw`);
      toast.success("Tabellone svuotato");
      fetchDraw();
    } catch (e) { toast.error("Errore"); }
    finally { setClearing(false); }
  };

  const handleUndo = async () => {
    try {
      await api.post(`/tournaments/${tournament.tournament_id}/categories/${selectedCat}/draw/undo`);
      toast.success("Operazione annullata");
      fetchDraw();
    } catch (e) { toast.error("Nessuna operazione da annullare"); }
  };

  const handleRedo = async () => {
    try {
      await api.post(`/tournaments/${tournament.tournament_id}/categories/${selectedCat}/draw/redo`);
      toast.success("Operazione ripristinata");
      fetchDraw();
    } catch (e) { toast.error("Nessuna operazione da ripetere"); }
  };

  const executeConfirmedAction = () => {
    if (!confirmAction) return;
    if (confirmAction.type === "generate") generateDraw();
    else if (confirmAction.type === "clear") {
      // First confirmation passed → ask for a SECOND confirmation
      setConfirmAction({ type: "clear_final", label: "ULTIMA conferma eliminazione" });
      return;
    }
    else if (confirmAction.type === "clear_final") clearDraw();
    else if (confirmAction.type === "empty") setShowEmptyDialog(true);
    setConfirmAction(null);
  };

  const handleEditPlayerName = ({ playerId, currentName, matchId, position }) => {
    setEditingSlot({ matchId, position, currentPlayerId: playerId, currentName });
    setSelectedPlayerId("");
  };

  const handleMatchClick = (match) => {
    setSelectedMatch(match);
    setWinnerId(match.winner_id || "");
    setMatchScore(match.score || "");
    setSchedDate(match.scheduled_date || "");
    setSchedTime(match.scheduled_time || "");
    setSchedCourt(match.scheduled_court != null ? String(match.scheduled_court) : "");
    setStartTime(match.start_time || "");
    setEndTime(match.end_time || "");
    const p1Real = match.player1_id && match.player1_id !== "BYE";
    const p2Real = match.player2_id && match.player2_id !== "BYE";
    setDialogMode(p1Real && p2Real ? "result" : "schedule");
  };

  const submitResult = async () => {
    if (!winnerId) { toast.error("Seleziona il vincitore"); return; }
    try {
      await api.put(`/tournaments/${tournament.tournament_id}/matches/${selectedMatch.match_id}`, { winner_id: winnerId, score: matchScore });
      // Save start_time and end_time if provided; backend auto-computes duration.
      if (startTime || endTime) {
        const timePayload = {};
        if (startTime) timePayload.start_time = startTime;
        if (endTime) timePayload.end_time = endTime;
        await api.put(`/tournaments/${tournament.tournament_id}/matches/${selectedMatch.match_id}/reschedule`, timePayload);
      }
      toast.success("Risultato registrato!");
      setSelectedMatch(null); setWinnerId(""); setMatchScore(""); fetchDraw();
    } catch (e) { toast.error("Errore"); }
  };

  const submitSchedule = async () => {
    if (!schedDate && !schedTime && !schedCourt && !startTime && !endTime) {
      toast.error("Inserisci almeno un campo"); return;
    }
    setScheduling(true);
    try {
      const payload = {};
      if (schedDate) payload.scheduled_date = schedDate;
      if (schedTime) payload.scheduled_time = schedTime;
      if (schedCourt) payload.scheduled_court = parseInt(schedCourt, 10);
      if (startTime) payload.start_time = startTime;
      if (endTime) payload.end_time = endTime;
      await api.put(`/tournaments/${tournament.tournament_id}/matches/${selectedMatch.match_id}/reschedule`, payload);
      toast.success("Programmazione aggiornata!");
      setSelectedMatch(null);
      fetchDraw();
    } catch (e) { toast.error(e.message || "Errore"); } finally { setScheduling(false); }
  };

  const submitSwap = async () => {
    if (!swapPlayerId) { toast.error("Seleziona un giocatore"); return; }
    try {
      await api.put(`/tournaments/${tournament.tournament_id}/matches/${selectedMatch.match_id}/swap`, {
        match_id: selectedMatch.match_id, position: swapPosition, new_player_id: swapPlayerId,
      });
      toast.success(swapPlayerId === "BYE" ? "BYE assegnato!" : swapPlayerId === "TBD" ? "Impostato come TBD" : "Giocatore assegnato!");
      setSelectedMatch(null); setSwapPlayerId(""); fetchDraw();
    } catch (e) { toast.error("Errore"); }
  };

  const savePlayerSwap = async () => {
    if (!selectedPlayerId) { toast.error("Seleziona un giocatore"); return; }
    try {
      await api.put(`/tournaments/${tournament.tournament_id}/matches/${editingSlot.matchId}/swap`, {
        match_id: editingSlot.matchId, position: editingSlot.position, new_player_id: selectedPlayerId,
      });
      toast.success(selectedPlayerId === "BYE" ? "BYE assegnato!" : selectedPlayerId === "TBD" ? "Impostato come TBD" : "Giocatore assegnato!");
      setEditingSlot(null); setSelectedPlayerId(""); fetchDraw();
    } catch (e) { toast.error("Errore nell'assegnazione"); }
  };

  const generateAllDraw = async () => {
    setGeneratingAll(true);
    try {
      const r = await api.post(`/tournaments/${tournament.tournament_id}/brackets/generate-all`, {});
      setAllGenerationResult(r);
      fetchDraw();
      if (r.total_categories > 0) toast.success(`${r.total_categories} tabelloni generati`);
      if (r.errors?.length) toast.warning(`${r.errors.length} categorie con errori`);
    } catch (e) {
      toast.error("Errore nella generazione in blocco");
    } finally {
      setGeneratingAll(false);
    }
  };


  return (
    <Card className="bg-white">
      <CardHeader>
        <CardTitle className="font-heading text-lg font-bold uppercase mb-3">Tabellone</CardTitle>
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex items-center gap-3 flex-1 flex-wrap">
            <Select value={selectedCat} onValueChange={setSelectedCat}>
              <SelectTrigger data-testid="category-select-bracket" className="w-64 rounded-sm"><SelectValue placeholder="Seleziona categoria" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__" className="font-semibold text-amber-700">
                  Tutte le categorie
                </SelectItem>
                <div className="border-t border-slate-100 my-1" />
                {tournament.categories.map((c) => <SelectItem key={c.category_id} value={c.category_id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {selectedCat === "__all__" ? (
              /* ── MODALITÀ "TUTTE LE CATEGORIE" ── */
              <Button
                data-testid="generate-all-brackets-btn"
                onClick={generateAllDraw}
                disabled={generatingAll || tournament.categories.length === 0}
                className="bg-amber-600 hover:bg-amber-700 text-white font-bold uppercase rounded-sm gap-1"
                size="sm"
              >
                {generatingAll ? <Loader2 className="h-4 w-4 animate-spin" /> : <Users className="h-4 w-4" />}
                Genera per Ranking – Tutte
              </Button>
            ) : (
              /* ── MODALITÀ SINGOLA CATEGORIA ── */
              <>
                <Button data-testid="create-empty-draw-btn" onClick={() => setConfirmAction({ type: "empty", label: "Crea nuovo tabellone" })} disabled={!selectedCat || generating} className="bg-green-700 hover:bg-green-800 text-white font-bold uppercase rounded-sm gap-1" size="sm">
                  <Plus className="h-4 w-4" /> Crea vuoto
                </Button>
                <Button data-testid="generate-draw-btn" onClick={() => setConfirmAction({ type: "generate", label: "Genera tabellone casuale" })} disabled={!selectedCat || generating} className="bg-green-700 hover:bg-green-800 text-white font-bold uppercase rounded-sm gap-1" size="sm">
                  <RefreshCw className="h-4 w-4" /> Casuale
                </Button>
                <Button data-testid="generate-seeded-btn" onClick={() => openSeedDialog()} disabled={!selectedCat || generating || catPlayers.length < 2} className="bg-green-700 hover:bg-green-800 text-white font-bold uppercase rounded-sm gap-1" size="sm">
                  <Trophy className="h-4 w-4" /> Con TdS
                </Button>
                <Button data-testid="generate-ranking-btn" onClick={() => setShowRankingDialog(true)} disabled={!selectedCat || generating || catPlayers.length < 2} className="bg-green-700 hover:bg-green-800 text-white font-bold uppercase rounded-sm gap-1" size="sm">
                  <Users className="h-4 w-4" /> Per Ranking
                </Button>
                {drawData?.matches?.length > 0 && (
                  <>
                    <Button data-testid="clear-draw-btn" onClick={() => setConfirmAction({ type: "clear", label: "Elimina tabellone" })} disabled={clearing} size="sm" className="bg-red-600 hover:bg-red-700 text-white font-bold uppercase rounded-sm gap-1">
                      <Trash2 className="h-3.5 w-3.5" /> Elimina
                    </Button>
                    <div className="flex items-center gap-1 border-l border-slate-200 pl-3 ml-1">
                      <Button data-testid="undo-btn" onClick={handleUndo} disabled={!historyInfo.can_undo} variant="outline" size="sm" className="rounded-sm h-8 w-8 p-0" title="Annulla">
                        <Undo2 className="h-4 w-4" />
                      </Button>
                      <Button data-testid="redo-btn" onClick={handleRedo} disabled={!historyInfo.can_redo} variant="outline" size="sm" className="rounded-sm h-8 w-8 p-0" title="Ripeti">
                        <Redo2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
        {/* Secondary row: Vista Completa + PDF + Note (only visible when a bracket exists) */}
        {selectedCat !== "__all__" && drawData?.matches?.length > 0 && (
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <Link to={`/tournament/${tournament.tournament_id}/bracket/${selectedCat}`} className="flex-1 min-w-[120px]">
              <Button variant="outline" size="sm" className="w-full rounded-sm gap-1"><ExternalLink className="h-4 w-4" /> Vista Completa</Button>
            </Link>
            <Button
              data-testid="export-bracket-pdf-btn"
              variant="outline"
              size="sm"
              className="flex-1 min-w-[80px] rounded-sm gap-1 text-green-700 border-green-200 hover:bg-green-50"
              onClick={(e) => {
                e.stopPropagation();
                const catName = tournament.categories.find(c => c.category_id === selectedCat)?.name || "";
                const schedMap = {};
                for (const sm of allMatches) {
                  if (sm.match_id) schedMap[sm.match_id] = sm;
                }
                const enrichedDraw = {
                  ...drawData,
                  matches: (drawData?.matches || []).map(m => {
                    const sched = schedMap[m.match_id];
                    if (sched) {
                      return {
                        ...m,
                        scheduled_date: sched.scheduled_date || m.scheduled_date,
                        scheduled_time: sched.scheduled_time || m.scheduled_time,
                        scheduled_court: sched.scheduled_court ?? m.scheduled_court,
                      };
                    }
                    return m;
                  }),
                };
                buildPdfVenue(venue)
                  .then((pdfVenue) =>
                    exportBracketPDF(tournament, enrichedDraw, catName, pdfVenue, tournament.pdf_bracket_notes || "")
                  )
                  .catch(err => {
                    console.error("PDF export error:", err);
                    toast.error("Errore nell'esportazione PDF: " + (err.message || "Errore sconosciuto"));
                  });
              }}
            >
              <FileDown className="h-4 w-4" /> PDF
            </Button>
            <Button
              data-testid="edit-bracket-notes-btn"
              variant="outline"
              size="sm"
              className="flex-1 min-w-[100px] rounded-sm gap-1"
              onClick={() => setShowNotesDialog(true)}
            >
              <FileText className="h-4 w-4" /> Note PDF
            </Button>
          </div>
        )}
      </CardHeader>
      <CardContent>
        {drawData?.matches?.length > 0 ? (
          <div className="border border-slate-200 rounded-sm p-2 overflow-hidden">
            <TournamentBracket matches={enrichedMatches} rounds={drawData.rounds} onMatchClick={handleMatchClick} onEditPlayerName={handleEditPlayerName} conflictSet={conflictSet} courtNames={tournament.court_names || {}} />
          </div>
        ) : (
          <div className="text-center py-12">
            <Trophy className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 mb-4">Nessun tabellone. Crea un tabellone vuoto o genera automaticamente.</p>
          </div>
        )}

        {/* Create Empty Bracket Dialog */}
        <Dialog open={showEmptyDialog} onOpenChange={setShowEmptyDialog}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader><DialogTitle className="font-heading text-xl font-bold uppercase">Crea Nuovo Tabellone</DialogTitle></DialogHeader>
            <div className="space-y-4 pt-2">
              <div>
                <Label className="text-xs font-bold uppercase">Dimensione Tabellone</Label>
                <Select value={emptySize} onValueChange={setEmptySize}>
                  <SelectTrigger data-testid="empty-draw-size-select" className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DRAW_SIZES.map(s => <SelectItem key={s} value={String(s)}>{s} giocatori</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-slate-400 mt-1">Tutti gli slot saranno vuoti. Potrai assegnare giocatori e BYE manualmente.</p>
              </div>
              <Button data-testid="confirm-empty-draw-btn" onClick={createEmptyDraw} disabled={generating} className="w-full bg-green-700 hover:bg-green-800 text-white font-bold uppercase rounded-sm">
                {generating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Crea Tabellone
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Seed Selection Dialog */}
        <Dialog open={showSeedDialog} onOpenChange={setShowSeedDialog}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading text-xl font-bold uppercase">Genera con Teste di Serie</DialogTitle></DialogHeader>
            <div className="space-y-4 pt-2">
              <div className="bg-green-50 border border-green-200 rounded-sm p-3 text-sm">
                <p className="text-green-700 text-xs font-bold uppercase">Assegna TdS</p>
                <p className="text-green-700 text-xs mt-1">Seleziona le TdS per ogni sezione. Usa le frecce per riordinare (TdS 1 in cima).</p>
              </div>

              {catPlayers.length > 0 && (() => {
                const sections = { "4.NC": [], "4.x": [], "3.x": [], "2.x": [], "1.x": [] };
                const secMap = { "4.NC": ["4.NC"], "4.x": ["4.6","4.5","4.4","4.3","4.2","4.1"], "3.x": ["3.6","3.5","3.4","3.3","3.2","3.1"], "2.x": ["2.8","2.7","2.6","2.5","2.4","2.3","2.2","2.1"], "1.x": ["1.6","1.5","1.4","1.3","1.2","1.1"] };
                const rv = { "1.1": 100, "1.2": 99, "1.3": 98, "1.4": 97, "1.5": 96, "1.6": 95, "2.1": 90, "2.2": 89, "2.3": 88, "2.4": 87, "2.5": 86, "2.6": 85, "2.7": 84, "2.8": 83, "3.1": 80, "3.2": 79, "3.3": 78, "3.4": 77, "3.5": 76, "3.6": 75, "4.1": 70, "4.2": 69, "4.3": 68, "4.4": 67, "4.5": 66, "4.6": 65, "4.NC": 50 };
                for (const p of catPlayers) {
                  const r = p.ranking || "4.NC";
                  for (const [sec, ranks] of Object.entries(secMap)) {
                    if (ranks.includes(r)) { sections[sec].push(p); break; }
                  }
                }
                // Sort each section by ranking (strongest first)
                for (const sec in sections) {
                  sections[sec].sort((a, b) => (rv[b.ranking] || 0) - (rv[a.ranking] || 0));
                }
                const activeSections = ["4.NC", "4.x", "3.x", "2.x", "1.x"].filter(sec => sections[sec].length > 0);

                const addSeedToSection = (sec, pid) => {
                  const p = sections[sec].find(pl => pl.player_id === pid);
                  if (!p) return;
                  const current = manualSeedsMap[sec] || [];
                  if (current.find(s => s.player_id === pid)) return;
                  setManualSeedsMap(prev => ({ ...prev, [sec]: [...(prev[sec] || []), { player_id: p.player_id, name: p.name, ranking: p.ranking }] }));
                };
                const removeSeedFromSection = (sec, pid) => {
                  setManualSeedsMap(prev => ({ ...prev, [sec]: (prev[sec] || []).filter(s => s.player_id !== pid) }));
                };
                const moveSeedInSection = (sec, idx, dir) => {
                  const arr = [...(manualSeedsMap[sec] || [])];
                  const newIdx = idx + dir;
                  if (newIdx < 0 || newIdx >= arr.length) return;
                  [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
                  setManualSeedsMap(prev => ({ ...prev, [sec]: arr }));
                };

                return (
                  <div className="space-y-3">
                    {activeSections.map((sec, idx) => {
                      const count = sections[sec].length;
                      const isLast = idx === activeSections.length - 1;
                      const secSeeds = manualSeedsMap[sec] || [];
                      const seedIds = new Set(secSeeds.map(s => s.player_id));
                      const availablePlayers = sections[sec].filter(p => !seedIds.has(p.player_id));
                      return (
                        <div key={sec} className="border border-slate-200 rounded-sm p-3 space-y-2">
                          <div className="flex items-center gap-2">
                            <Badge className="rounded-sm bg-green-700 text-white text-xs">{sec}</Badge>
                            <span className="text-xs text-slate-500">{count} giocatori</span>
                          </div>

                          {/* Manual seeds (auto-mode removed) */}
                          <>
                              {/* Current seeds for this section */}
                              {secSeeds.length > 0 && (
                                <div className="space-y-1">
                                  {secSeeds.map((s, si) => (
                                    <div key={s.player_id} className="flex items-center gap-1.5 bg-green-50 border border-green-200 rounded-sm px-2 py-1">
                                      <span className="text-[10px] font-bold text-green-700 w-5 text-center">T{si + 1}</span>
                                      <span className="text-xs font-medium flex-1">{s.name}</span>
                                      <span className="text-[10px] text-slate-400">{s.ranking}</span>
                                      <button data-testid={`seed-up-${sec}-${si}`} onClick={() => moveSeedInSection(sec, si, -1)} disabled={si === 0} className="p-0.5 text-slate-400 hover:text-slate-600 disabled:opacity-30">
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 15l-6-6-6 6"/></svg>
                                      </button>
                                      <button data-testid={`seed-down-${sec}-${si}`} onClick={() => moveSeedInSection(sec, si, 1)} disabled={si === secSeeds.length - 1} className="p-0.5 text-slate-400 hover:text-slate-600 disabled:opacity-30">
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M6 9l6 6 6-6"/></svg>
                                      </button>
                                      <button data-testid={`seed-remove-${sec}-${si}`} onClick={() => removeSeedFromSection(sec, s.player_id)} className="p-0.5 text-red-400 hover:text-red-600">
                                        <Trash2 className="h-3 w-3" />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}
                              {/* Add seed selector */}
                              {availablePlayers.length > 0 && (
                                <Select onValueChange={(pid) => addSeedToSection(sec, pid)} value="">
                                  <SelectTrigger data-testid={`add-seed-${sec}`} className="h-7 rounded-sm text-xs">
                                    <SelectValue placeholder="+ Aggiungi TdS..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {availablePlayers.map(p => (
                                      <SelectItem key={p.player_id} value={p.player_id} className="text-xs">
                                        {p.name} ({p.ranking})
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              )}
                            </>

                          {!isLast && (
                            <div className="flex items-center gap-2 pt-1 border-t border-slate-100">
                              <Label className="text-[10px] font-bold uppercase whitespace-nowrap">N° Qualificati al prossimo turno</Label>
                              <Input
                                data-testid={`seeded-qualificati-${sec}`}
                                type="number"
                                min="1"
                                placeholder=""
                                value={qualificatiMap[sec] ?? ""}
                                onChange={(e) => setQualificatiMap(prev => ({ ...prev, [sec]: e.target.value === "" ? undefined : parseInt(e.target.value) || 1 }))}
                                className="h-8 rounded-sm text-xs w-20"
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
              <Button data-testid="confirm-seeded-draw-btn" onClick={generateSeededDraw} disabled={generating || catPlayers.length < 2} className="w-full bg-green-700 hover:bg-green-800 text-white font-bold uppercase rounded-sm">
                {generating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trophy className="h-4 w-4 mr-2" />} Genera Tabellone con TdS
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Ranking Draw Dialog */}
        <Dialog open={showRankingDialog} onOpenChange={setShowRankingDialog}>
          <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading text-xl font-bold uppercase">Genera per Ranking (Sezioni)</DialogTitle></DialogHeader>
            <div className="space-y-4 pt-2">
              <div className="bg-green-50 border border-green-200 rounded-sm p-4 text-sm">
                <p className="font-bold text-green-800 mb-2">Sistema a Sezioni</p>
                <p className="text-green-700 break-words">I giocatori verranno divisi in sezioni per ranking (4.NC, 4.x, 3.x, 2.x, 1.x). Ogni sezione genera un sotto-tabellone. I vincitori avanzano alla sezione successiva come qualificati.</p>
              </div>
              {catPlayers.length > 0 && (() => {
                const sections = { "4.NC": [], "4.x": [], "3.x": [], "2.x": [], "1.x": [] };
                const secMap = { "4.NC": ["4.NC"], "4.x": ["4.6","4.5","4.4","4.3","4.2","4.1"], "3.x": ["3.6","3.5","3.4","3.3","3.2","3.1"], "2.x": ["2.8","2.7","2.6","2.5","2.4","2.3","2.2","2.1"], "1.x": ["1.6","1.5","1.4","1.3","1.2","1.1"] };
                for (const p of catPlayers) {
                  const r = p.ranking || "4.NC";
                  for (const [sec, ranks] of Object.entries(secMap)) {
                    if (ranks.includes(r)) { sections[sec].push(p); break; }
                  }
                }
                const activeSections = ["4.NC", "4.x", "3.x", "2.x", "1.x"].filter(sec => sections[sec].length > 0);
                return (
                  <div className="space-y-3">
                    {activeSections.map((sec, idx) => {
                      const count = sections[sec].length;
                      const isLast = idx === activeSections.length - 1;
                      return (
                        <div key={sec} className="border border-slate-200 rounded-sm p-3 space-y-2">
                          <div className="flex items-center gap-2">
                            <Badge className="rounded-sm bg-green-700 text-white text-xs">{sec}</Badge>
                            <span className="text-xs text-slate-500">{count} giocatori</span>
                          </div>
                          <p className="text-xs text-slate-400 break-words">{sections[sec].map(p => p.name).join(", ")}</p>
                          {!isLast && (
                            <div className="flex items-center gap-2 pt-1">
                              <Label className="text-[10px] font-bold uppercase whitespace-nowrap">N° Qualificati al prossimo turno</Label>
                              <Input
                                data-testid={`qualificati-${sec}`}
                                type="number"
                                min="1"
                                placeholder=""
                                value={qualificatiMap[sec] ?? ""}
                                onChange={(e) => setQualificatiMap(prev => ({ ...prev, [sec]: e.target.value === "" ? undefined : parseInt(e.target.value) || 1 }))}
                                className="h-8 rounded-sm text-xs w-20"
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
              <Button data-testid="confirm-ranking-draw-btn" onClick={generateRankingDraw} disabled={generating} className="w-full bg-green-700 hover:bg-green-800 text-white font-bold uppercase rounded-sm">
                {generating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Users className="h-4 w-4 mr-2" />} Genera per Ranking
              </Button>
            </div>
          </DialogContent>
        </Dialog>


        {/* All-categories generation result dialog */}
        {allGenerationResult && (
          <Dialog open={!!allGenerationResult} onOpenChange={() => setAllGenerationResult(null)}>
            <DialogContent className="sm:max-w-md max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl font-bold uppercase">Riepilogo Generazione</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-2">
                {allGenerationResult.generated?.length > 0 && (
                  <div>
                    <p className="text-xs font-bold uppercase text-green-700 mb-2">Generati con successo ({allGenerationResult.total_categories})</p>
                    <div className="space-y-1.5">
                      {allGenerationResult.generated.map((g) => (
                        <div key={g.category_id} className="flex items-center justify-between bg-green-50 border border-green-100 rounded-sm px-3 py-2">
                          <span className="text-sm font-medium">{g.category}</span>
                          <span className="text-xs text-slate-500">{g.total_matches} incontri · {g.sections} sez.</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {allGenerationResult.errors?.length > 0 && (
                  <div>
                    <p className="text-xs font-bold uppercase text-red-600 mb-2">Errori ({allGenerationResult.errors.length})</p>
                    <div className="space-y-1.5">
                      {allGenerationResult.errors.map((err, i) => (
                        <div key={i} className="bg-red-50 border border-red-100 rounded-sm px-3 py-2 text-xs text-red-700">{err}</div>
                      ))}
                    </div>
                  </div>
                )}
                <Button onClick={() => setAllGenerationResult(null)} className="w-full bg-slate-700 hover:bg-slate-800 text-white font-bold uppercase rounded-sm">Chiudi</Button>
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* Edit Player from Category Dialog */}
        <Dialog open={!!editingSlot} onOpenChange={() => { setEditingSlot(null); setSelectedPlayerId(""); }}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl font-bold uppercase">
                {editingSlot?.currentPlayerId ? "Sostituisci Giocatore" : "Assegna Giocatore"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              {editingSlot?.currentName && (
                <div className="bg-slate-50 rounded-sm p-3 text-center text-sm">
                  <p className="text-slate-500">Giocatore attuale:</p>
                  <p className="font-medium">{editingSlot.currentName}</p>
                </div>
              )}
              <div>
                <Label className="text-xs font-bold uppercase">Seleziona Giocatore</Label>
                <Select value={selectedPlayerId} onValueChange={setSelectedPlayerId}>
                  <SelectTrigger data-testid="edit-select-player-bracket" className="mt-1 rounded-sm"><SelectValue placeholder="Scegli dalla lista..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="BYE" className="text-slate-400 italic">BYE (salta turno)</SelectItem>
                    <SelectItem value="TBD" className="text-amber-500">TBD (da definire)</SelectItem>
                    {catPlayers.map(p => <SelectItem key={p.player_id} value={p.player_id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button data-testid="save-player-swap-bracket-btn" onClick={savePlayerSwap} className="w-full bg-green-700 hover:bg-green-800 text-white font-bold uppercase rounded-sm">Assegna Giocatore</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Result Dialog - Full Tabbed Version */}
        <Dialog open={!!selectedMatch} onOpenChange={() => setSelectedMatch(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl font-bold uppercase">
                {dialogMode === "result" ? "Inserisci Risultato" : dialogMode === "schedule" ? "Riprogramma Incontro" : "Modifica Giocatore"}
              </DialogTitle>
            </DialogHeader>
            {selectedMatch && (
              <div>
                <Tabs value={dialogMode} onValueChange={setDialogMode}>
                  <TabsList className="w-full mb-4">
                    <TabsTrigger value="schedule" className="flex-1 rounded-sm gap-1 text-xs">
                      <Clock className="h-3 w-3" /> Programmazione
                    </TabsTrigger>
                    <TabsTrigger value="result" className="flex-1 rounded-sm gap-1 text-xs" disabled={
                      !selectedMatch.player1_id || !selectedMatch.player2_id ||
                      selectedMatch.player1_id === "BYE" || selectedMatch.player2_id === "BYE"
                    }>
                      <Trophy className="h-3 w-3" /> Risultato
                    </TabsTrigger>
                    <TabsTrigger value="swap" className="flex-1 rounded-sm gap-1 text-xs">
                      <UserPlus className="h-3 w-3" /> Modifica
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="result">
                    <div className="space-y-4">
                      <div className="bg-slate-50 rounded-sm p-4 text-center">
                        <p className="text-sm font-medium">{selectedMatch.player1_name || "TBD"} <span className="text-slate-400 mx-2">vs</span> {selectedMatch.player2_name || "TBD"}</p>
                        <p className="text-xs text-slate-500 mt-1">{ROUND_LABELS[selectedMatch.round] || selectedMatch.round}</p>
                        <Button data-testid="td-result-goto-modifica-btn" variant="outline" size="sm" onClick={() => setDialogMode("swap")} className="mt-2 text-xs rounded-sm gap-1 border-slate-300 hover:border-green-500 hover:bg-green-50">
                          <Edit className="h-3 w-3" /> Modifica
                        </Button>
                      </div>
                      <div>
                        <Label className="text-xs font-bold uppercase">Vincitore</Label>
                        <Select value={winnerId} onValueChange={setWinnerId}>
                          <SelectTrigger data-testid="bracket-winner-select" className="mt-1 rounded-sm"><SelectValue placeholder="Seleziona vincitore" /></SelectTrigger>
                          <SelectContent>
                            {selectedMatch.player1_id && selectedMatch.player1_id !== "BYE" && <SelectItem value={selectedMatch.player1_id}>{selectedMatch.player1_name}</SelectItem>}
                            {selectedMatch.player2_id && selectedMatch.player2_id !== "BYE" && <SelectItem value={selectedMatch.player2_id}>{selectedMatch.player2_name}</SelectItem>}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs font-bold uppercase">Punteggio</Label>
                        <Input data-testid="bracket-score-input" value={matchScore} onChange={(e) => setMatchScore(e.target.value)} placeholder="es. 6-4 6-2" className="mt-1 rounded-sm" />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs font-bold uppercase">Ora Inizio <span className="text-slate-400 font-normal normal-case">(opzionale)</span></Label>
                          <Input data-testid="td-result-start-time" type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className="mt-1 rounded-sm" />
                        </div>
                        <div>
                          <Label className="text-xs font-bold uppercase">Ora Fine <span className="text-slate-400 font-normal normal-case">(opzionale)</span></Label>
                          <Input data-testid="td-result-end-time" type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} className="mt-1 rounded-sm" />
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button data-testid="bracket-submit-result-btn" onClick={submitResult} className="flex-1 bg-green-700 hover:bg-green-800 text-white font-bold uppercase rounded-sm">Salva Risultato</Button>
                        <Button data-testid="td-open-reschedule-btn" variant="outline" onClick={() => setDialogMode("schedule")} className="border-amber-500 text-amber-600 hover:bg-amber-50 font-bold uppercase rounded-sm text-xs">
                          <Clock className="h-3.5 w-3.5 mr-1" /> Riprogramma
                        </Button>
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent value="schedule">
                    <div className="space-y-3">
                      <div className="bg-slate-50 rounded-sm p-3 text-center text-sm">
                        <p className="font-medium">{selectedMatch.player1_name || "TBD"} <span className="text-slate-400 mx-2">vs</span> {selectedMatch.player2_name || "TBD"}</p>
                        <p className="text-xs text-slate-500 mt-1">{ROUND_LABELS[selectedMatch.round] || selectedMatch.round}</p>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs font-bold uppercase">Data</Label>
                          <Input data-testid="td-bracket-sched-date" type="date" value={schedDate} onChange={(e) => setSchedDate(e.target.value)} className="mt-1 rounded-sm" />
                        </div>
                        <div>
                          <Label className="text-xs font-bold uppercase">Campo</Label>
                          <Input data-testid="td-bracket-sched-court" type="number" min="1" value={schedCourt} onChange={(e) => setSchedCourt(e.target.value)} placeholder="es. 1" className="mt-1 rounded-sm" />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs font-bold uppercase">Orario Programmato</Label>
                        <Input data-testid="td-bracket-sched-time" type="time" value={schedTime} onChange={(e) => setSchedTime(e.target.value)} className="mt-1 rounded-sm" />
                      </div>
                      <Button data-testid="td-bracket-sched-submit" onClick={submitSchedule} disabled={scheduling} className="w-full bg-amber-600 hover:bg-amber-700 text-white font-bold uppercase rounded-sm">
                        {scheduling ? "Salvando..." : "Salva Programmazione"}
                      </Button>
                    </div>
                  </TabsContent>

                  <TabsContent value="swap">
                    <div className="space-y-4">
                      <div className="bg-slate-50 rounded-sm p-4 text-center text-sm">
                        <p>Partita: <strong>{selectedMatch.player1_name || "TBD"}</strong> vs <strong>{selectedMatch.player2_name || "TBD"}</strong></p>
                      </div>
                      <div>
                        <Label className="text-xs font-bold uppercase">Giocatore 1</Label>
                        <Select value={swapPosition || "player1"} onValueChange={(v) => setSwapPosition(v)}>
                          <SelectTrigger className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="player1">{selectedMatch.player1_name || "vuoto"}</SelectItem>
                            <SelectItem value="player2">{selectedMatch.player2_name || "vuoto"}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs font-bold uppercase">Giocatore 2</Label>
                        <Select value={swapPlayerId || ""} onValueChange={(v) => setSwapPlayerId(v)}>
                          <SelectTrigger data-testid="td-swap-player-select" className="mt-1 rounded-sm"><SelectValue placeholder="Seleziona giocatore" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="BYE" className="text-slate-400 italic">BYE (salta turno)</SelectItem>
                            <SelectItem value="TBD" className="text-amber-500">TBD (da definire)</SelectItem>
                            {catPlayers.map(p => <SelectItem key={p.player_id} value={p.player_id}>{p.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <Button data-testid="td-submit-swap-btn" onClick={submitSwap} className="w-full bg-green-700 hover:bg-green-800 text-white font-bold uppercase rounded-sm">Assegna Giocatore</Button>
                    </div>
                  </TabsContent>
                </Tabs>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Confirmation Alert */}
        <AlertDialog open={!!confirmAction} onOpenChange={(open) => { if (!open) setConfirmAction(null); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className={`font-heading text-lg font-bold uppercase ${confirmAction?.type === "clear_final" ? "text-red-700" : ""}`}>
                {confirmAction?.type === "clear_final" ? "Sei davvero sicuro?" : "Conferma Azione"}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {confirmAction?.type === "clear" && "Sei sicuro di voler eliminare il tabellone? Tutte le partite e i risultati verranno eliminati."}
                {confirmAction?.type === "clear_final" && (
                  <span className="font-semibold text-red-700">
                    ATTENZIONE: questa azione è IRREVERSIBILE. Tabellone, partite, punteggi e calendario verranno cancellati definitivamente. Procedo?
                  </span>
                )}
                {confirmAction?.type === "generate" && "Sei sicuro di voler rigenerare il tabellone? Il tabellone attuale verra' sostituito."}
                {confirmAction?.type === "empty" && "Sei sicuro di voler creare un nuovo tabellone? Il tabellone attuale verra' sostituito."}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-sm">Annulla</AlertDialogCancel>
              <AlertDialogAction
                data-testid="confirm-action-btn"
                onClick={executeConfirmedAction}
                className={`${confirmAction?.type === "clear" || confirmAction?.type === "clear_final" ? "bg-red-600 hover:bg-red-700" : "bg-green-700 hover:bg-green-800"} text-white rounded-sm`}
              >
                {confirmAction?.type === "clear_final" ? "Sì, elimina definitivamente" : "Conferma"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        <Dialog open={showNotesDialog} onOpenChange={setShowNotesDialog}>
          <DialogContent className="rounded-sm max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-lg font-bold uppercase">Note sul PDF Tabellone</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 pt-2">
              <p className="text-xs text-slate-500">Le note verranno stampate in fondo a ogni pagina del PDF del tabellone.</p>
              <Textarea
                data-testid="bracket-notes-textarea"
                value={bracketNotes}
                onChange={(e) => setBracketNotes(e.target.value)}
                placeholder="Es. ‘Tutte le partite si giocano al meglio dei 3 set‘ . Max ~4 righe."
                rows={5}
                className="rounded-sm resize-none"
              />
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setShowNotesDialog(false)} className="rounded-sm">Annulla</Button>
              <Button data-testid="save-bracket-notes-btn" onClick={saveBracketNotes} disabled={savingNotes} className="bg-green-700 hover:bg-green-800 text-white rounded-sm">
                {savingNotes ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}Salva note
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export { BracketPanel };
